import type { ITransformRT2, IVector2 } from '@seddi/math-v2';
import { type PieceLogoBase, LogoTechnique, PieceSide } from '@seddi/scene';
import {
  type MeshPhysicalMaterial,
  type Texture,
  BackSide,
  ClampToEdgeWrapping,
  DataTexture,
  FrontSide,
  LinearFilter,
  LinearMipMapLinearFilter,
  Matrix3,
  RepeatWrapping,
  RGBAFormat,
  TextureLoader,
  UnsignedByteType,
  UVMapping,
} from '@seddi/three';
import log from 'loglevel';

import { clone as cloneMaterial } from '../resources/material';
import type { ResourcesContext } from '../resources/resourcesContext';

import glitterNormalMap from '../../assets/glitter_normal.png';

enum WorkerCommand {
  NORMAL_FROM_COLOR = 'NORMAL_FROM_COLOR',
  SMOOTH_NORMAL_FROM_COLOR = 'SMOOTH_NORMAL_FROM_COLOR',
}

export type PieceLogoRenderable = {
  readonly id: string;
  material: MeshPhysicalMaterial;
  resourceRef?: string;
};

/**
 * Create piece logo.
 * @param id - Piece logo ID.
 */
export const create = (id: string): PieceLogoRenderable => {
  return { id } as PieceLogoRenderable;
};

/**
 * Get pixels in CPU.
 * @param texture Texture.
 */
const getTextureData = (texture: Texture): ImageData => {
  // This is the only way, right now, to get the data in the CPU for an imported image
  // https://stackoverflow.com/questions/68102995/is-it-possible-to-get-pixels-from-imagebitmap-web-object-without-a-canvas
  // The recommended way is to render it on a canvas
  // https://developer.mozilla.org/en-US/docs/Web/API/ImageBitmap

  const canvas = document.createElement('canvas');
  canvas.height = texture.image.height;
  canvas.width = texture.image.width;

  const context = canvas.getContext('2d');
  context.scale(1, -1);
  context.drawImage(texture.image, 0, -canvas.height, canvas.width, canvas.height);

  const data = context.getImageData(0, 0, canvas.width, canvas.height);
  return data;
};

const toClampedUint8Array = (nonClampedArray) => {
  return Uint8ClampedArray.from(nonClampedArray);
};

/**
 * Update logo materials.
 * @param renderable - Renderable.
 * @param desc - Piece logo.
 * @param resources - Resources context.
 */
export const attachLogo = (() => {
  const m0 = new Matrix3();

  return (renderable: PieceLogoRenderable, desc: PieceLogoBase, resources: ResourcesContext) => {
    if (renderable.resourceRef) {
      detachLogo(renderable, resources);
    }

    const resource = resources.getRefLogo(desc.resource)!;

    // Create material instance.
    const material = cloneMaterial(resource);
    material.polygonOffset = true;
    material.polygonOffsetFactor = 0;
    material.polygonOffsetUnits = -5;
    material.transparent = true;

    switch (desc.technique) {
      case LogoTechnique.Glitter: {
        const loader = new TextureLoader();
        material.metalness = 0.7;
        material.roughness = 0.2;
        material.specularIntensity = 0.8;
        loader.load(glitterNormalMap, (texture: Texture) => {
          texture.wrapS = RepeatWrapping;
          texture.wrapT = RepeatWrapping;
          // Init transform.
          // Glitter normal map tile represents 2.5cm squared.
          m0.makeScale(1.0 / 0.025, 1.0 / 0.025);
          // Inverse transform required?
          // m0.multiply(_matrix1);
          // m0.multiply(_matrix0);
          texture.matrix.copy(m0);
          texture.matrixAutoUpdate = false;
          material.normalMap = texture;
          material.needsUpdate = true;
          // Notify draw required.
          resources.needsUpdate = true;
        });
        break;
      }
      case LogoTechnique.HighDensity: {
        material.roughness = 0.6;
        material.specularIntensity = 0.2;
        const t0 = performance.now();

        // Recover albedo data in CPI
        const texture = material.map;
        const isDataImg = texture.image.data != undefined;
        const data = !isDataImg ? getTextureData(texture).data : toClampedUint8Array(texture.image.data);
        const height = texture.image.height;
        const width = texture.image.width;

        // Parse number of channels
        let channels = 0;
        if (texture.format === RGBAFormat) {
          channels = 4;
        } else {
          log.error('Unsupported albedo map');
          return;
        }

        // Create worker.
        const worker = new Worker(PIECE_LOGO_RENDERABLE_WORKER_URL);
        // Connect normal map.
        worker.onmessage = (event: MessageEvent<Uint8Array<ArrayBuffer>>) => {
          const normalMap = new DataTexture(
            event.data,
            width,
            height,
            RGBAFormat,
            UnsignedByteType,
            UVMapping,
            ClampToEdgeWrapping,
            ClampToEdgeWrapping,
            LinearFilter,
            LinearMipMapLinearFilter,
          );
          normalMap.flipY = material.map.flipY;
          normalMap.matrix.copy(material.map.matrix);
          normalMap.matrixAutoUpdate = false;
          normalMap.needsUpdate = true;
          material.normalMap = normalMap;
          material.needsUpdate = true;
          log.info(`Build high density maps took ${performance.now() - t0} ms`);
          // Request draw.
          resources.needsUpdate = true;
          // Dispose.
          worker.terminate();
        };
        // Compute normal map.
        worker.postMessage({
          type: WorkerCommand.NORMAL_FROM_COLOR,
          payload: { channels, data, height, width },
        });
        break;
      }
      case LogoTechnique.Puff: {
        material.roughness = 0.6;
        material.specularIntensity = 0.2;
        const t0 = performance.now();

        // Recover albedo data.
        const baseColor = material.map;
        const isDataImg = baseColor.image.data != undefined;
        const data = !isDataImg ? getTextureData(baseColor).data : toClampedUint8Array(baseColor.image.data);
        const height = baseColor.image.height;
        const width = baseColor.image.width;

        // Change
        let channels = 0;
        if (baseColor.format === RGBAFormat) {
          channels = 4;
        } else {
          log.error('Unsupported albedo map');
          return;
        }

        // Create worker.
        const worker = new Worker(PIECE_LOGO_RENDERABLE_WORKER_URL);
        // Connect normal map.
        worker.onmessage = (event: MessageEvent<Uint8Array<ArrayBuffer>>) => {
          const normalMap = new DataTexture(
            event.data,
            width,
            height,
            RGBAFormat,
            UnsignedByteType,
            UVMapping,
            ClampToEdgeWrapping,
            ClampToEdgeWrapping,
            LinearFilter,
            LinearMipMapLinearFilter,
          );
          normalMap.flipY = material.map.flipY;
          normalMap.matrix.copy(material.map.matrix);
          normalMap.matrixAutoUpdate = false;
          normalMap.needsUpdate = true;
          material.normalMap = normalMap;
          material.needsUpdate = true;
          log.info(`Build puff maps took ${performance.now() - t0} ms`);
          // Request draw.
          resources.needsUpdate = true;
          // Dispose.
          worker.terminate();
        };
        // Compute normal map.
        worker.postMessage({
          type: WorkerCommand.SMOOTH_NORMAL_FROM_COLOR,
          payload: { channels, data, height, width },
        });
        break;
      }
      case LogoTechnique.HeatTransfer:
      case LogoTechnique.DirectToGarment:
      case LogoTechnique.Rubber: {
        // No reparametrization required.
        break;
      }
      case LogoTechnique.Waterbased: {
        material.roughness = 0.6;
        material.specularIntensity = 0.2;
        break;
      }
      default: {
        log.warn('Using unimplemented logo technique.');
      }
    }

    renderable.material = material;
    renderable.resourceRef = desc.resource;

    updateSide(renderable, desc.side);
    updateTransform(renderable, desc);
  };
})();

/**
 * Update logo side.
 * @param mesh Piece mesh.
 * @param logo Piece logo.
 */
export const updateSide = (renderable: PieceLogoRenderable, side: PieceSide) => {
  const material = renderable.material;
  if (!material) {
    return;
  }
  const isBack = side === PieceSide.BACK;
  material.side = isBack ? BackSide : FrontSide;
};

/**
 * Update logo transform.
 * @param mesh Piece mesh.
 * @param placement Piece logo placement.
 */
export const updateTransform = (() => {
  const _matrix0 = new Matrix3();
  const _matrix1 = new Matrix3();
  const _matrix2 = new Matrix3();
  const _matrix3 = new Matrix3();
  const _matrix4 = new Matrix3();

  return (
    renderable: PieceLogoRenderable,
    placement: {
      side?: PieceSide;
      size: IVector2;
      technique: LogoTechnique;
      transform: ITransformRT2;
    },
  ) => {
    const material = renderable.material;
    if (!material) {
      return;
    }

    const { side, size, transform } = placement;
    const flip = side === PieceSide.BACK ? -1 : 1;

    _matrix0.makeTranslation(-transform.translation.x, -transform.translation.y);
    _matrix1.makeRotation(-transform.rotation);
    _matrix2.makeScale(1.0 / size.x, 1.0 / size.y);
    _matrix3.makeScale(flip, 1);
    _matrix4.makeTranslation(0.5, 0.5);
    _matrix4.multiply(_matrix3);
    _matrix4.multiply(_matrix2);
    _matrix4.multiply(_matrix1);
    _matrix4.multiply(_matrix0);

    const map = material.map;
    map.matrix.copy(_matrix4);
    map.matrixAutoUpdate = false;
    const normalMap = material.normalMap;
    if (normalMap) {
      normalMap.matrix.copy(_matrix4);
      normalMap.matrixAutoUpdate = false;
    }
  };
})();

/**
 * Detach logo.
 * @param renderable - Renderable.
 * @param resources - Resources context.
 */
export const detachLogo = (renderable: PieceLogoRenderable, resources: ResourcesContext) => {
  if (!renderable.resourceRef) {
    return;
  }

  {
    const material = renderable.material;
    // dispose normal map texture
    if (material.normalMap) {
      const normalMap = material.normalMap;
      normalMap.dispose();
      material.normalMap = null;
    }
    // dispose material
    material.dispose();
  }

  resources.retRefLogo(renderable.resourceRef);
  delete renderable.resourceRef;
};

/**
 * Dispose piece logo.
 * @param renderable - Renderable.
 * @param resources - Resources context.
 */
export const dispose = (renderable: PieceLogoRenderable, resources: ResourcesContext) => {
  detachLogo(renderable, resources);
};

/**
 * Update logo visibility.
 * @param renderable - Renderable.
 * @param visible - Whether logo should be visible.
 */
export const updateVisibility = (renderable: PieceLogoRenderable, visible: boolean) => {
  if (renderable.material) {
    renderable.material.visible = visible;
    renderable.material.needsUpdate = true;
  }
};
